import { Check, ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useId, useState } from 'react';
import { Link } from 'react-router';
import { searchStyleGroups, styleName } from '../data/styles';
import { useActiveProject, useProjects } from '../features/beat/projectsStore';
import { BottomSheet } from '../ui/BottomSheet';
import { Icon } from '../ui/Icon';
import { useAppStore, useStyleId } from './store';
import styles from './Shell.module.css';

/** Style picker: 2 taps to change style everywhere (DESIGN_SYSTEM §6). Search is optional, so the budget is unchanged. */
function StylePicker() {
  const styleId = useStyleId();
  const setStyle = useAppStore((s) => s.setStyle);
  const showToast = useAppStore((s) => s.showToast);
  const project = useActiveProject();
  const setProjectStyle = useProjects((s) => s.setProjectStyle);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchId = useId();
  const name = styleName(styleId);
  const groups = searchStyleGroups(query);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);

  return (
    <>
      <button
        type="button"
        className={styles.picker}
        aria-haspopup="dialog"
        aria-label={`Style: ${name}. Change style`}
        onClick={() => setOpen(true)}
      >
        <span className={styles.pickerName}>{name}</span>
        <Icon icon={ChevronDown} />
      </button>
      <BottomSheet open={open} title="Style" onClose={close}>
        <label htmlFor={searchId} className="visually-hidden">Search styles</label>
        <input
          id={searchId}
          type="search"
          className={styles.search}
          placeholder="Search styles"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {groups.length === 0 && <p className="t-body">No styles match "{query}".</p>}
        {groups.map((g) => (
          <div key={g.group} role="radiogroup" aria-labelledby={`style-group-${g.group}`}>
            <h3 id={`style-group-${g.group}`} className={`t-small ${styles.groupLabel}`}>{g.label}</h3>
            {g.styles.map((s) => (
              <button
                key={s.id}
                type="button"
                role="radio"
                aria-checked={s.id === styleId}
                className={styles.styleRow}
                onClick={() => {
                  setStyle(s.id);
                  close();
                  // The app style changes at once; the project's changes only if you ask (SPEC F1, DECISIONS 2026-09-23)
                  if (project && project.styleId !== s.id) {
                    showToast({
                      message: `Style set to ${s.name}`,
                      action: { label: `Also apply to ${project.name}`, run: () => void setProjectStyle(project.id, s.id) },
                    });
                  }
                }}
              >
                {s.name}
                {s.id === styleId && <Icon icon={Check} />}
              </button>
            ))}
          </div>
        ))}
      </BottomSheet>
    </>
  );
}

/** Active project: 1 tap to resume (DESIGN_SYSTEM §6). With none, the chip starts one. */
function ProjectChip() {
  const project = useActiveProject();
  const loaded = useProjects((s) => s.projects !== undefined);
  useEffect(() => { void useProjects.getState().load(); }, []);
  if (!loaded) return null;
  if (!project) {
    return (
      <Link to="/beat?new=1" className={styles.chip} aria-label="No active project. New project">
        <span className={styles.chipName}>New project</span>
      </Link>
    );
  }
  return (
    <Link to={`/beat/${project.id}`} className={styles.chip} aria-label={`Active project: ${project.name}. Open project home`}>
      <span className={styles.chipName}>{project.name}</span>
    </Link>
  );
}

export function Header() {
  return (
    <header className={styles.header}>
      <StylePicker />
      <ProjectChip />
    </header>
  );
}
